import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search,
  Grid3X3,
  List,
  Eye,
  Calendar,
  ChevronDown,
  Play,
  ArrowUpRight,
  X,
} from 'lucide-react';
import VideoCard from '../components/VideoCard';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { cn } from '../lib/utils';
import { contentService } from '../services/contentService.js';

const SORT_OPTIONS = [
  { id: 'newest', name: 'Newest', icon: Calendar },
  { id: 'popular', name: 'Most viewed', icon: Eye },
];

/** Map URL ?type= values to works.type enum */
const resolveContentType = (typeParam) => {
  if (typeParam === 'movie') return 'movie';
  if (typeParam === 'series') return 'tv_show';
  return null;
};

/* ─────────────────────────────────────────────
   FEATURED EDITORIAL STRIP — single hero feature
   with a thin tick-rail carousel underneath.
   ───────────────────────────────────────────── */
const FeaturedStrip = ({ items, index, onIndex, onOpen }) => {
  const current = items[index];
  if (!current) return null;

  const primaryGenre =
    (current.genre && current.genre[0]) ||
    (current.genres && current.genres[0]) ||
    'Featured';

  return (
    <section className="relative">
      <article
        onClick={() => onOpen(current)}
        className="group relative grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-0 overflow-hidden rounded-sm border border-border/40 bg-card/30 cursor-pointer hover:border-primary/40 transition-colors"
      >
        {/* Image */}
        <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[340px] overflow-hidden">
          {current.backgroundImage ? (
            <img
              src={current.backgroundImage}
              alt={current.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.03]"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-card to-card" />
          )}
          {/* Cinematic gradient */}
          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-background/85 via-background/30 to-transparent" />

          {/* Mono tag */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="text-[9px] tracking-[0.28em] uppercase bg-primary/90 text-primary-foreground px-2.5 py-1 rounded-sm">
              Featured
            </span>
          </div>

          {/* Hover play affordance */}
          <div className="absolute bottom-4 right-4 hidden md:inline-flex opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase bg-background/80 backdrop-blur-sm border border-border/50 px-3 py-1.5 rounded-full text-foreground">
              <Play className="h-3 w-3 fill-current" />
              Play
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="relative flex flex-col justify-between p-5 md:p-8 gap-5">
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] tracking-[0.22em] uppercase text-primary">
                {primaryGenre}
              </span>
              {current.year && (
                <>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                  <span className="text-[13px] text-muted-foreground">
                    {current.year}
                  </span>
                </>
              )}
              {current.duration && (
                <>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                  <span className="text-[13px] text-muted-foreground">
                    {current.duration}
                  </span>
                </>
              )}
            </div>
            <h3 className="text-2xl md:text-[2rem] lg:text-[2.25rem] leading-[1.05] tracking-tight font-semibold text-foreground group-hover:text-primary transition-colors">
              {current.title}
            </h3>
            {current.description && (
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {current.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              size="default"
              className="h-10 px-5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(current);
              }}
            >
              <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
              Watch now
            </Button>
            <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground tabular-nums">
              {String(index + 1).padStart(2, '0')}
              <span className="text-muted-foreground/50"> / {String(items.length).padStart(2, '0')}</span>
            </span>
          </div>
        </div>
      </article>

      {/* Tick rail */}
      {items.length > 1 && (
        <div className="flex items-center gap-2 mt-4">
          {items.map((item, i) => {
            const isActive = i === index;
            return (
              <button
                key={item.id}
                onClick={() => onIndex(i)}
                aria-label={`View feature ${i + 1}`}
                className="group/tick flex-1 py-2"
              >
                <div
                  className={cn(
                    'h-[2px] w-full rounded-full transition-colors',
                    isActive
                      ? 'bg-primary'
                      : 'bg-border/40 group-hover/tick:bg-border/70'
                  )}
                />
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

/* ─────────────────────────────────────────────
   BROWSE PAGE
   ───────────────────────────────────────────── */
const Browse = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const contentType = resolveContentType(typeParam);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedGenre, setSelectedGenre] = useState(
    searchParams.get('genre') || 'all'
  );
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [showAllGenres, setShowAllGenres] = useState(false);
  const [content, setContent] = useState([]);
  const [genres, setGenres] = useState([]);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const genreRailRef = useRef(null);

  useEffect(() => {
    contentService.getGenres().then(setGenres);
    contentService.getFeaturedContent(5).then(setFeaturedContent);
  }, []);

  // Keep URL in sync with search + genre (preserve type from sidebar nav)
  useEffect(() => {
    const params = {};
    if (searchQuery.trim()) params.q = searchQuery.trim();
    if (selectedGenre !== 'all') params.genre = selectedGenre;
    if (typeParam === 'movie' || typeParam === 'series') params.type = typeParam;
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedGenre, typeParam, setSearchParams]);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        let results;
        if (searchQuery.trim()) {
          results = await contentService.searchContent(searchQuery, 50, contentType);
        } else {
          const genre = selectedGenre === 'all' ? null : selectedGenre;
          results = await contentService.getRecentContent({
            genre,
            limit: 50,
            status: 'published',
            sortBy,
            type: contentType,
          });
        }
        setContent(results);
        setError(null);
      } catch (err) {
        console.error('Error loading browse content:', err);
        setError('Failed to load content. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    const timeoutId = setTimeout(loadContent, searchQuery ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedGenre, sortBy, contentType]);

  const openFeatured = (item) => {
    navigate(item.slug ? `/watch/${item.slug}` : `/video/${item.id}`);
  };

  const activeFilters = [];
  if (searchQuery.trim()) activeFilters.push(`“${searchQuery.trim()}”`);
  if (selectedGenre !== 'all') activeFilters.push(selectedGenre);

  const visibleGenres = showAllGenres ? genres : genres.slice(0, 8);

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-hidden">
      <style>{`
        @keyframes browse-reveal {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .reveal { animation: browse-reveal 0.6s cubic-bezier(.16,1,.3,1) both; }
        .grain::before {
          content: '';
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          opacity: 0.03;
          mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
      `}</style>
      <div className="grain" />

      {/* ════════════════════════════════════════
          EDITORIAL MASTHEAD
         ════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-20 w-[560px] h-[560px] rounded-full bg-primary/[0.07] blur-[120px]" />
          <div className="absolute -bottom-40 right-0 w-[420px] h-[420px] rounded-full bg-primary/[0.04] blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-12 md:pt-16 pb-8 md:pb-10">
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-[10px] tracking-[0.22em] uppercase text-primary/80">
              Catalogue
            </span>
            <div className="h-px flex-1 bg-border/60" />
            <button
              onClick={() => navigate('/home')}
              className="group inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
            >
              <span className="border-b border-border/50 group-hover:border-primary pb-0.5">
                Back to home
              </span>
              <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div className="max-w-2xl">
              <h1 className="font-bold text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[4.25rem] leading-[1] tracking-tight">
                Everything worth
                <br />
                <span className="text-muted-foreground">watching.</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-lg">
                Hand-picked films, series, and documentaries from independent creators.
              </p>
            </div>

            {/* Search field — editorial */}
            <form
              className="relative w-full md:max-w-sm md:shrink-0"
              onSubmit={(e) => e.preventDefault()}
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search titles, creators…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 pr-10 h-12 rounded-full bg-card/40 border-border/50 focus:border-primary/50 focus:bg-card/70 text-sm placeholder:text-muted-foreground/70"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-card/60 text-muted-foreground hover:text-foreground inline-flex items-center justify-center"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          FEATURED STRIP (hidden while searching)
         ════════════════════════════════════════ */}
      {!searchQuery && !contentType && featuredContent.length > 0 && (
        <section className="reveal max-w-7xl mx-auto px-4 sm:px-6 pt-10 md:pt-14">
          <header className="mb-6 md:mb-7">
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-primary/80">
                Tonight's pick
              </span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
              <h2 className="text-xl font-semibold">
                Featured
              </h2>
              <p className="text-sm text-muted-foreground sm:text-right max-w-sm">
                The one we'd cue up first.
              </p>
            </div>
          </header>
          <FeaturedStrip
            items={featuredContent}
            index={featuredIndex}
            onIndex={setFeaturedIndex}
            onOpen={openFeatured}
          />
        </section>
      )}

      {/* ════════════════════════════════════════
          RESULTS
         ════════════════════════════════════════ */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <header className="mb-6 md:mb-7">
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-[10px] tracking-[0.22em] uppercase text-primary/80">
              All titles
            </span>
            <div className="h-px flex-1 bg-border/60" />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <h2 className="text-xl font-semibold">
              {searchQuery ? 'Search results' : 'The catalogue'}
            </h2>
            <p className="text-sm text-muted-foreground sm:text-right max-w-sm">
              {searchQuery
                ? 'What we found for you.'
                : 'Filter by genre or sort by what matters.'}
            </p>
          </div>
        </header>

        {/* ── Genre rail ── */}
        {genres.length > 0 && (
          <div className="relative -mx-4 sm:mx-0 mb-6">
            <div
              ref={genreRailRef}
              className="flex items-center gap-4 sm:gap-5 border-b border-border/40 pb-3 px-4 sm:px-0 overflow-x-auto no-scrollbar"
            >
              <GenreChip
                label="All"
                isActive={selectedGenre === 'all'}
                onClick={() => setSelectedGenre('all')}
                index={0}
                total={genres.length + 1}
              />
              {visibleGenres.map((g, i) => (
                <GenreChip
                  key={g.id || g.name}
                  label={g.name}
                  isActive={selectedGenre === g.name}
                  onClick={() => setSelectedGenre(g.name)}
                  index={i + 1}
                />
              ))}
              {!showAllGenres && genres.length > 8 && (
                <button
                  onClick={() => setShowAllGenres(true)}
                  className="group inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-muted-foreground hover:text-primary transition-colors whitespace-nowrap shrink-0 pb-1"
                >
                  <span className="border-b border-border/50 group-hover:border-primary pb-px">
                    More ({genres.length - 8})
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Controls row ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-7">
          {/* Active state + count */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground tabular-nums">
              {loading
                ? 'Loading…'
                : `${String(content.length).padStart(2, '0')} result${content.length === 1 ? '' : 's'}`}
            </span>
            {activeFilters.length > 0 && (
              <>
                <span className="hidden sm:inline h-3 w-px bg-border/60" />
                <div className="flex items-center gap-2 flex-wrap">
                  {activeFilters.map((f) => (
                    <span
                      key={f}
                      className="text-[13px] text-foreground/80"
                    >
                      {f}
                    </span>
                  ))}
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedGenre('all');
                    }}
                    className="text-[10px] tracking-[0.22em] uppercase text-primary hover:text-primary/80 border-b border-primary/40 hover:border-primary pb-px"
                  >
                    Clear
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Sort + view */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                Sort
              </span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-card/40 border border-border/50 rounded-full pl-3 pr-8 h-8 text-xs text-foreground hover:border-primary/50 focus:border-primary/60 focus:outline-none"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              </div>
            </div>

            <div className="flex items-center rounded-full border border-border/50 bg-card/40 p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                className={cn(
                  'h-7 w-8 rounded-full inline-flex items-center justify-center transition-colors',
                  viewMode === 'grid'
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Grid3X3 className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                aria-label="List view"
                className={cn(
                  'h-7 w-8 rounded-full inline-flex items-center justify-center transition-colors',
                  viewMode === 'list'
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Grid / list ── */}
        {error ? (
          <EmptyState
            title="Something went wrong"
            message={error}
            actionLabel="Reload"
            onAction={() => window.location.reload()}
          />
        ) : loading ? (
          <div
            className={cn(
              'grid gap-5 md:gap-6',
              viewMode === 'grid'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1'
            )}
          >
            {Array.from({ length: 8 }).map((_, index) => (
              <SkeletonLoader
                key={`skeleton-${index}`}
                variant="video"
                className="reveal"
                style={{ animationDelay: `${index * 50}ms` }}
              />
            ))}
          </div>
        ) : content.length > 0 ? (
          viewMode === 'grid' ? (
            <div className="grid gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {content.map((item, index) => (
                <VideoCard
                  key={item.id}
                  video={item}
                  className="reveal"
                  style={{ animationDelay: `${Math.min(index, 12) * 50}ms` }}
                />
              ))}
            </div>
          ) : (
            <ol className="divide-y divide-border/40 border-y border-border/40">
              {content.map((item, index) => (
                <BrowseRow
                  key={item.id}
                  item={item}
                  index={index + 1}
                  onOpen={openFeatured}
                />
              ))}
            </ol>
          )
        ) : (
          <EmptyState
            title="An empty frame"
            message={
              searchQuery
                ? `Nothing turned up for “${searchQuery}”. Try loosening the filters.`
                : "We're still curating this cut. Try another genre."
            }
            actionLabel="Clear all filters"
            onAction={() => {
              setSearchQuery('');
              setSelectedGenre('all');
            }}
          />
        )}
      </section>
    </div>
  );
};

/* ─────────────────────────────────────────────
   SUB-COMPONENTS
   ───────────────────────────────────────────── */

const GenreChip = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'group relative text-[0.95rem] leading-none pb-1 transition-colors whitespace-nowrap shrink-0',
      isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
    )}
  >
    <span>{label}</span>
    <span
      className={cn(
        'absolute left-0 right-0 -bottom-[13px] h-px transition-all',
        isActive ? 'bg-primary' : 'bg-transparent group-hover:bg-border'
      )}
    />
  </button>
);

const BrowseRow = ({ item, index, onOpen }) => {
  const genre =
    (item.tags && item.tags[0]) ||
    (item.genre && item.genre[0]) ||
    (item.genres && item.genres[0]);
  const creator =
    item.creator?.name ||
    item.creator?.username ||
    item.studio?.name ||
    'Unknown';

  return (
    <li
      onClick={() => onOpen(item)}
      className="group grid grid-cols-[64px_112px_1fr] sm:grid-cols-[64px_148px_1fr_auto] items-center gap-4 sm:gap-5 py-4 cursor-pointer transition-colors hover:bg-card/30 -mx-2 px-2"
    >
      <span className="text-2xl md:text-3xl leading-none font-light tabular-nums text-muted-foreground/30 group-hover:text-primary/60 transition-colors">
        {String(index).padStart(2, '0')}
      </span>
      <div className="relative aspect-[16/10] overflow-hidden rounded-sm border border-border/40 bg-card/40">
        {item.thumbnail || item.backgroundImage ? (
          <img
            src={item.thumbnail || item.backgroundImage}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-card to-card" />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          {genre && (
            <span className="text-[9px] tracking-[0.22em] uppercase text-primary">
              {genre}
            </span>
          )}
        </div>
        <h4 className="text-base md:text-lg leading-tight tracking-tight font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors">
          {item.title}
        </h4>
        <p className="text-[13px] text-muted-foreground mt-1 line-clamp-1">
          {creator}
          {item.year ? ` · ${item.year}` : ''}
          {item.duration ? ` · ${item.duration}` : ''}
        </p>
      </div>
      <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
        {item.views && item.views !== '0' && (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Eye className="h-3 w-3" />
            {item.views}
          </span>
        )}
        <Play className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-colors" />
      </div>
    </li>
  );
};

const EmptyState = ({ title, message, actionLabel, onAction }) => (
  <div className="border border-dashed border-border/50 rounded-sm p-10 md:p-14 text-center">
    <div className="text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-3">
      An empty frame
    </div>
    <h3 className="text-2xl md:text-3xl leading-tight font-semibold mb-2">
      {title}
    </h3>
    <p className="text-muted-foreground max-w-md mx-auto mb-6">
      {message}
    </p>
    {onAction && (
      <button
        onClick={onAction}
        className="text-[10px] tracking-[0.22em] uppercase px-4 py-2 border border-border/60 rounded-full hover:border-primary hover:text-primary transition-colors"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

export default Browse;
